type AnyWindow = Window & {
  SpeechRecognition?: typeof window.SpeechRecognition;
  webkitSpeechRecognition?: typeof window.SpeechRecognition;
};

export function getSpeechRecognition(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null;
  const w = window as AnyWindow;
  return (w.SpeechRecognition || w.webkitSpeechRecognition) ?? null;
}

export function isSpeechSupported(): boolean {
  return getSpeechRecognition() !== null;
}

export interface SpeechSession {
  stop: () => void;
}

export interface SpeechHandlers {
  onResult: (transcript: string) => void;
  onError?: (message: string) => void;
  onEnd?: () => void;
}

/**
 * Starts a one-shot Web Speech recognition session.
 * Returns a SpeechSession with .stop(), or null if speech isn't supported.
 */
export function startSpeech(handlers: SpeechHandlers): SpeechSession | null {
  const Ctor = getSpeechRecognition();
  if (!Ctor) {
    handlers.onError?.('Voice input not supported in this browser. Try Chrome, Edge, or Safari.');
    return null;
  }

  const recognition = new Ctor();
  recognition.lang = 'en-US';
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;

  recognition.onresult = (event: SpeechRecognitionEvent) => {
    const transcript = event.results?.[0]?.[0]?.transcript ?? '';
    handlers.onResult(transcript.trim());
  };

  recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
    handlers.onError?.(event.error || 'Speech recognition failed.');
  };

  recognition.onend = () => {
    handlers.onEnd?.();
  };

  try {
    recognition.start();
  } catch (err) {
    handlers.onError?.((err as Error)?.message || 'Could not start microphone.');
    return null;
  }

  return {
    stop: () => {
      try {
        recognition.stop();
      } catch {
        // ignore
      }
    },
  };
}
